import CustomListClient from '../client/custom-list/CustomListClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import {
  CustomListBulkRecordType,
  CustomListCreateRequest,
  CustomListDbRecordType,
  CustomListModel,
  CustomListRecordDeleteAllRequest,
  CustomListRecordExportRequest,
  CustomListRecordFilter,
  CustomListRecordFilterField,
  CustomListRecordItem,
  CustomListRecordSearchRequest,
  CustomListRecordSearchResult,
  CustomListTabType,
} from '../domain/custom-list/CustomListDomains';
import {
  CustomFilter,
  CustomFilterCategorical,
  CustomFilterDateSearch,
  CustomFilterField,
  CustomFilterLiteralSearch,
} from '../domain/ui/FilterDomains';
import {generateUUIDV4} from '../util/Generator';
import moment from 'moment';

class CustomListService {
  private readonly customListClient: CustomListClient;

  public constructor(customListClient?: CustomListClient) {
    this.customListClient = customListClient ?? GatewayContainer.getInstance().get(GatewayNames.CUSTOM_LIST_CLIENT);
  }

  public create(body: CustomListCreateRequest): Promise<CustomListModel> {
    return this.customListClient.create(body);
  }

  public getAll(): Promise<CustomListModel[]> {
    return this.customListClient.getAll();
  }

  public getById(listId: string): Promise<CustomListModel> {
    return this.customListClient.getById(listId);
  }

  public findListsByRecordId(recordId: string): Promise<CustomListModel[]> {
    return this.customListClient.findListsByRecordId(recordId);
  }

  public delete(listId: string): Promise<void> {
    return this.customListClient.delete(listId);
  }

  public deleteRecord(listId: string, recordId: string): Promise<void> {
    return this.customListClient.deleteRecord(listId, recordId);
  }

  public deleteRecords(listId: string, body: CustomListRecordDeleteAllRequest): Promise<{deleted: number}> {
    return this.customListClient.deleteRecords(listId, body);
  }

  public addBulk(listId: string, type: CustomListBulkRecordType, file: File): Promise<void> {
    return this.customListClient.addBulk(listId, type, file);
  }

  public searchRecords(listId: string, body: CustomListRecordSearchRequest): Promise<CustomListRecordSearchResult> {
    return this.customListClient.searchRecords(listId, body);
  }

  public async getRecordCount(listId: string): Promise<number> {
    try {
      const result: {total: number} = await this.customListClient.countAllRecords(listId);
      return result.total || 0;
    } catch (e) {
      console.error(e);
      return 0;
    }
  }

  public getRecordById(listId: string, recordId: string): Promise<CustomListRecordItem> {
    return this.customListClient.getRecordById(listId, recordId);
  }

  public async exportRecords(listId: string, body: CustomListRecordExportRequest): Promise<void> {
    const blob: Blob = await this.customListClient.exportRecords(listId, body);
    const fileName: string = this.resolveExportFileName(listId, body.recordType);
    this.downloadBlob(blob, fileName);
  }

  public createSlugId(name: string): string {
    const transliterated: string = this.transliterateTurkish(name.trim().toLowerCase());
    const slug: string = transliterated
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (slug.length > 0) {
      return slug;
    }
    return generateUUIDV4();
  }

  public toDbRecordType(tab: CustomListTabType): CustomListDbRecordType {
    switch (tab) {
      case 'entity':
        return 'ENTITY';
      case 'individual':
      default:
        return 'INDIVIDUAL';
    }
  }

  public toBulkRecordType(tab: CustomListTabType): CustomListBulkRecordType {
    switch (tab) {
      case 'entity':
        return 'Entity';
      case 'individual':
      default:
        return 'Individual';
    }
  }

  public filterLists(lists: CustomListModel[], filter: CustomFilter | undefined): CustomListModel[] {
    if (filter == undefined || filter.fields == undefined || filter.fields.length === 0) {
      return lists;
    }

    const enabledFields: CustomFilterField[] = filter.fields.filter((field: CustomFilterField) => field.enabled);
    if (enabledFields.length === 0) {
      return lists;
    }

    return lists.filter((list: CustomListModel) => {
      const matches: boolean[] = enabledFields.map((field: CustomFilterField) => this.matchesListField(list, field));
      switch (filter.type) {
        case 'ANY_MATCH':
          return matches.some((match: boolean) => match);
        case 'NONE_MATCH':
          return matches.every((match: boolean) => !match);
        case 'ALL_MATCH':
        default:
          return matches.every((match: boolean) => match);
      }
    });
  }

  public mapUiFilterToRequest(filter: CustomFilter | undefined): CustomListRecordFilter | undefined {
    if (filter == undefined || filter.fields == undefined) {
      return undefined;
    }

    const fields: CustomListRecordFilterField[] = filter.fields
      .filter((field: CustomFilterField) => field.enabled)
      .map((field: CustomFilterField) => this.mapFilterField(field))
      .filter((field: CustomListRecordFilterField | undefined): field is CustomListRecordFilterField => field != undefined);

    if (fields.length === 0) {
      return undefined;
    }

    return {
      type: filter.type,
      fields,
    };
  }

  private mapFilterField(field: CustomFilterField): CustomListRecordFilterField | undefined {
    switch (field.search.type) {
      case 'LITERAL': {
        const value: CustomFilterLiteralSearch = field.search.value as CustomFilterLiteralSearch;
        if (value.value === undefined || value.value === null || value.value === '') {
          return undefined;
        }
        return {
          field: field.field,
          enabled: field.enabled,
          searchType: 'LITERAL',
          operator: value.operator,
          value: value.value,
        };
      }
      case 'CATEGORICAL': {
        const value: CustomFilterCategorical = field.search.value as CustomFilterCategorical;
        if (value.values == undefined || value.values.length === 0) {
          return undefined;
        }
        return {
          field: field.field,
          enabled: field.enabled,
          searchType: 'CATEGORICAL',
          values: value.values,
        };
      }
      case 'DATE': {
        const value: CustomFilterDateSearch = field.search.value as CustomFilterDateSearch;
        if (value.startDate == undefined || value.endDate == undefined) {
          return undefined;
        }
        const startDate: number = moment(value.startDate.value).valueOf();
        const endDate: number = value.endDate.type === 'Now'
          ? moment().valueOf()
          : moment(value.endDate.value).valueOf();
        return {
          field: field.field,
          enabled: field.enabled,
          searchType: 'DATE',
          startDate,
          endDate,
        };
      }
      default:
        return undefined;
    }
  }

  private matchesListField(list: CustomListModel, field: CustomFilterField): boolean {
    switch (field.search.type) {
      case 'LITERAL':
        return this.matchesListLiteral(list, field);
      case 'DATE':
        return this.matchesListDate(list, field);
      case 'CATEGORICAL':
      default:
        return false;
    }
  }

  private matchesListLiteral(list: CustomListModel, field: CustomFilterField): boolean {
    const search: CustomFilterLiteralSearch = field.search.value as CustomFilterLiteralSearch;
    const rawValue: string | undefined = this.resolveListLiteralValue(list, field.field);
    if (rawValue == undefined) {
      return false;
    }
    const source: string = rawValue.toLowerCase();
    const target: string = String(search.value ?? '').toLowerCase();
    switch (search.operator) {
      case 'eq':
        return source === target;
      case 'neq':
        return source !== target;
      case 'contains':
        return source.includes(target);
      case 'startsWith':
        return source.startsWith(target);
      case 'endsWith':
        return source.endsWith(target);
      default:
        return source.includes(target);
    }
  }

  private matchesListDate(list: CustomListModel, field: CustomFilterField): boolean {
    const search: CustomFilterDateSearch = field.search.value as CustomFilterDateSearch;
    if (search.startDate == undefined || search.endDate == undefined) {
      return false;
    }
    const value: number | undefined = this.resolveListDateValue(list, field.field);
    if (value == undefined) {
      return false;
    }
    const startDate: number = moment(search.startDate.value).valueOf();
    const endDate: number = search.endDate.type === 'Now'
      ? moment().valueOf()
      : moment(search.endDate.value).valueOf();
    return value >= startDate && value <= endDate;
  }

  private resolveListLiteralValue(list: CustomListModel, field: string): string | undefined {
    switch (field) {
      case 'name':
        return list.name;
      case 'description':
        return list.description ?? '';
      default:
        return undefined;
    }
  }

  private resolveListDateValue(list: CustomListModel, field: string): number | undefined {
    switch (field) {
      case 'createdAt':
        return list.createdAt;
      case 'updatedAt':
        return list.updatedAt;
      default:
        return undefined;
    }
  }

  private resolveExportFileName(listId: string, recordType: CustomListDbRecordType): string {
    switch (recordType) {
      case 'ENTITY':
        return `${listId}-entities.csv`;
      case 'INDIVIDUAL':
      default:
        return `${listId}-individuals.csv`;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url: string = URL.createObjectURL(blob);
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private transliterateTurkish(value: string): string {
    return value
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/ı/g, 'i')
      .replace(/i̇/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ş/g, 's')
      .replace(/ü/g, 'u');
  }
}

export default CustomListService;
